import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../shared/components/CompanyPeriodSelector';
import { useLanguage } from '../../app/i18n/LanguageContext';
import ifrsService from '../../services/ifrs';
import { buildFinancialPositionPresentation, formatFinancialStatementAmount } from './financialPositionPresentation';
import { exportFinancialPositionToExcel, exportFinancialPositionToPdf } from './financialPositionExports';
import { buildProfitLossOciPresentation } from './profitLossOciPresentation';
import { exportProfitLossOciToExcel, exportProfitLossOciToPdf } from './profitLossOciExports';
import { buildCashFlowPresentation } from './cashFlowPresentation';
import { exportCashFlowToExcel, exportCashFlowToPdf } from './cashFlowExports';
import { buildEquityChangesPresentation } from './equityChangesPresentation';
import { exportEquityChangesToExcel, exportEquityChangesToPdf } from './equityChangesExports';
import { buildNotesPresentation } from './notesPresentation';
import { exportNotesToExcel, exportNotesToPdf } from './notesExports';
import { buildVukIfrsComparisonPresentation } from './vukIfrsComparisonPresentation';
import { exportVukIfrsComparisonToExcel, exportVukIfrsComparisonToPdf } from './vukIfrsComparisonExports';
import { isTenantAdminOrSystemAdmin } from '../../shared/auth/access';
import './IfrsWorkspacePage.css';

const REPORT_LABELS = {
  financialPosition: {
    en: 'Financial Position Statement',
    tr: 'Finansal Durum Tablosu'
  },
  profitLossOci: {
    en: 'Profit or Loss + OCI',
    tr: 'Kar veya Zarar + OCI'
  },
  cashFlow: {
    en: 'Cash Flow Statement',
    tr: 'Nakit Akis Tablosu'
  },
  equityChanges: {
    en: 'Statement of Changes in Equity',
    tr: 'Ozkaynak Degisim Tablosu'
  },
  vukIfrsComparison: {
    en: 'Local GAAP to IFRS Comparison Report',
    tr: 'VUK-IFRS Karsilastirma Raporu'
  },
  notes: {
    en: 'Notes',
    tr: 'Dipnotlar'
  }
};

function areReportsComparable(currentReport, previousReport) {
  if (!currentReport || !previousReport) {
    return false;
  }

  const currentSource = String(currentReport.sourceTrace || '').trim().toLowerCase();
  const previousSource = String(previousReport.sourceTrace || '').trim().toLowerCase();
  const currentVersion = String(currentReport.batchVersion || '').trim().toUpperCase();
  const previousVersion = String(previousReport.batchVersion || '').trim().toUpperCase();

  // Worksheet V2 outputs aggregated statement lines; legacy approved-batch rows are not schema-compatible.
  if (currentSource !== previousSource) {
    return false;
  }
  if (currentVersion === 'WORKSHEET_V2' || previousVersion === 'WORKSHEET_V2') {
    return currentVersion === previousVersion;
  }
  return true;
}

function IfrsReportModulePage({ reportType }) {
  const { language } = useLanguage();
  const isTr = !String(language || '').toLowerCase().startsWith('en');
  const L = useCallback((en, tr) => (isTr ? tr : en), [isTr]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '' });
  const [selectionMeta, setSelectionMeta] = useState({
    companyName: '',
    currentPeriodLabel: '',
    previousPeriodLabel: ''
  });
  const [reportResponse, setReportResponse] = useState(null);
  const [comparativeReportResponse, setComparativeReportResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const canClearIfrsData = useMemo(() => isTenantAdminOrSystemAdmin(), []);

  const reportLabel = useMemo(() => {
    const label = REPORT_LABELS[reportType] || REPORT_LABELS.financialPosition;
    return isTr ? label.tr : label.en;
  }, [isTr, reportType]);
  const supportsProfessionalPresentation =
    reportType === 'financialPosition'
    || reportType === 'profitLossOci'
    || reportType === 'cashFlow'
    || reportType === 'equityChanges'
    || reportType === 'vukIfrsComparison'
    || reportType === 'notes';

  const clearFeedback = useCallback(() => {
    setMessage('');
    setError('');
  }, []);

  const formatAmount = useCallback((value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return String(value ?? '-');
    }
    return new Intl.NumberFormat(isTr ? 'tr-TR' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(parsed);
  }, [isTr]);

  const mapAccountType = useCallback((type) => {
    if (!isTr) return type;
    const map = {
      ASSET: 'VARLIK',
      LIABILITY: 'YUKUMLULUK',
      EQUITY: 'OZKAYNAK',
      INCOME: 'GELIR',
      EXPENSE: 'GIDER',
      OCI: 'DIGER KAPSAMLI GELIR',
      REVENUE: 'HASILAT',
      COST: 'MALIYET',
      TAX: 'VERGI',
      UNKNOWN: 'BILINMEYEN'
    };
    return map[String(type || '').toUpperCase()] || type;
  }, [isTr]);

  const mapSourceTrace = useCallback((trace) => {
    if (!isTr) return trace;
    const map = {
      ifrs_trial_balance_materialized: 'IFRS mizan (materyalize)',
      ifrs_trial_balance: 'IFRS mizan',
      vuk_trial_balance: 'VUK mizan',
      manual_entry: 'manuel giris',
      manual: 'manuel',
      transformation_rule: 'donusum kurali',
      adjustment: 'duzeltme',
      opening_balance: 'acilis bakiyesi',
      system_generated: 'sistem uretimi'
    };
    const key = String(trace || '').toLowerCase().trim();
    return map[key] || trace;
  }, [isTr]);

  const handleSelectionChange = useCallback((nextSelection) => {
    setSelection({ companyId: nextSelection.companyId || '', periodId: nextSelection.periodId || '' });
    setReportResponse(null);
    setComparativeReportResponse(null);
    clearFeedback();
    setSelectionMeta((prev) => ({
      ...prev,
      companyName: nextSelection.company?.name || '',
      currentPeriodLabel: nextSelection.period?.label
        || nextSelection.period?.description
        || nextSelection.period?.code
        || '',
      previousPeriodLabel: ''
    }));
  }, [clearFeedback]);

  const handleLoadReport = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) return;
    clearFeedback();
    setLoading(true);
    try {
      if (!supportsProfessionalPresentation) {
        const data = await ifrsService.fetchIfrsReport({
          companyId: selection.companyId,
          periodId: selection.periodId,
          reportType
        });
        setReportResponse(data);
        setComparativeReportResponse(null);
        setMessage(L(`${reportLabel} loaded. Rows: ${data?.lines?.length || 0}`, `${reportLabel} yuklendi. Satir: ${data?.lines?.length || 0}`));
        return;
      }

      if (reportType === 'vukIfrsComparison') {
        const data = await ifrsService.fetchIfrsReport({
          companyId: selection.companyId,
          periodId: selection.periodId,
          reportType
        });
        setReportResponse(data);
        setComparativeReportResponse(null);
        setMessage(L(`${reportLabel} loaded. Rows: ${data?.lines?.length || 0}`, `${reportLabel} yuklendi. Satir: ${data?.lines?.length || 0}`));
        return;
      }

      const periods = await ifrsService.fetchCompanyPeriods({ companyId: selection.companyId });
      const currentPeriodIndex = periods.findIndex((period) => String(period.id) === String(selection.periodId));
      const previousPeriod = currentPeriodIndex >= 0 ? periods[currentPeriodIndex + 1] : null;

      const [currentResult, previousResult] = await Promise.allSettled([
        ifrsService.fetchIfrsReport({
          companyId: selection.companyId,
          periodId: selection.periodId,
          reportType
        }),
        previousPeriod
          ? ifrsService.fetchIfrsReport({
              companyId: selection.companyId,
              periodId: previousPeriod.id,
              reportType
            })
          : Promise.resolve(null)
      ]);

      if (currentResult.status !== 'fulfilled') {
        throw currentResult.reason;
      }

      const currentData = currentResult.value;
      const rawPreviousData = previousResult.status === 'fulfilled' ? previousResult.value : null;
      const previousData = areReportsComparable(currentData, rawPreviousData) ? rawPreviousData : null;
      setReportResponse(currentData);
      setComparativeReportResponse(previousData);
      setSelectionMeta((prev) => ({
        ...prev,
        previousPeriodLabel: previousData
          ? (previousPeriod?.label || previousPeriod?.description || previousPeriod?.code || '')
          : ''
      }));

      const comparativeText = previousData?.lines?.length
        ? L('Comparative previous period loaded.', 'Karsilastirmali onceki donem yuklendi.')
        : L('Comparative previous period not available.', 'Karsilastirmali onceki donem bulunamadi.');
      setMessage(L(
        `${reportLabel} loaded. Rows: ${currentData?.lines?.length || 0}. ${comparativeText}`,
        `${reportLabel} yuklendi. Satir: ${currentData?.lines?.length || 0}. ${comparativeText}`
      ));
    } catch (caught) {
      setError(caught.message || L('IFRS report could not be loaded.', 'IFRS raporu yuklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [L, clearFeedback, reportLabel, reportType, selection.companyId, selection.periodId, supportsProfessionalPresentation]);

  useEffect(() => {
    if (!selection.companyId || !selection.periodId) {
      return;
    }
    handleLoadReport();
  }, [handleLoadReport, selection.companyId, selection.periodId]);

  const presentation = useMemo(() => {
    if (!reportResponse || !supportsProfessionalPresentation) {
      return null;
    }
    if (reportType === 'financialPosition') {
      return buildFinancialPositionPresentation({
        currentReport: reportResponse,
        previousReport: comparativeReportResponse,
        currentPeriodLabel: selectionMeta.currentPeriodLabel,
        previousPeriodLabel: selectionMeta.previousPeriodLabel,
        isTr
      });
    }
    if (reportType === 'cashFlow') {
      return buildCashFlowPresentation({
        currentReport: reportResponse,
        previousReport: comparativeReportResponse,
        currentPeriodLabel: selectionMeta.currentPeriodLabel,
        previousPeriodLabel: selectionMeta.previousPeriodLabel,
        isTr
      });
    }
    if (reportType === 'equityChanges') {
      return buildEquityChangesPresentation({
        currentReport: reportResponse,
        previousReport: comparativeReportResponse,
        currentPeriodLabel: selectionMeta.currentPeriodLabel,
        previousPeriodLabel: selectionMeta.previousPeriodLabel,
        isTr
      });
    }
    if (reportType === 'notes') {
      return buildNotesPresentation({
        currentReport: reportResponse,
        previousReport: comparativeReportResponse,
        currentPeriodLabel: selectionMeta.currentPeriodLabel,
        previousPeriodLabel: selectionMeta.previousPeriodLabel,
        isTr
      });
    }
    if (reportType === 'vukIfrsComparison') {
      return buildVukIfrsComparisonPresentation({
        reportResponse,
        isTr
      });
    }
    return buildProfitLossOciPresentation({
      currentReport: reportResponse,
      previousReport: comparativeReportResponse,
      currentPeriodLabel: selectionMeta.currentPeriodLabel,
      previousPeriodLabel: selectionMeta.previousPeriodLabel,
      isTr
    });
  }, [comparativeReportResponse, isTr, reportResponse, reportType, selectionMeta.currentPeriodLabel, selectionMeta.previousPeriodLabel, supportsProfessionalPresentation]);

  const exportFilenameBase = useMemo(() => {
    const companyPart = (selectionMeta.companyName || 'company').replace(/\s+/g, '-').toLowerCase();
    const periodPart = (selectionMeta.currentPeriodLabel || selection.periodId || 'period').replace(/\s+/g, '-').toLowerCase();
    const reportPart = reportType === 'profitLossOci'
      ? 'profit-loss-oci'
      : reportType === 'cashFlow'
        ? 'cash-flow'
        : reportType === 'equityChanges'
          ? 'equity-changes'
        : reportType === 'vukIfrsComparison'
          ? 'vuk-ifrs-comparison'
        : reportType === 'notes'
          ? 'notes'
        : 'financial-position';
    return `ifrs-${reportPart}-${companyPart}-${periodPart}`;
  }, [reportType, selection.periodId, selectionMeta.companyName, selectionMeta.currentPeriodLabel]);

  const handleExport = useCallback(async (format) => {
    if (!presentation) return;
    clearFeedback();
    setExporting(true);
    try {
      const companyName = selectionMeta.companyName || '';
      if (reportType === 'financialPosition') {
        if (format === 'excel') {
          await exportFinancialPositionToExcel({
            presentation,
            filename: `${exportFilenameBase}.xlsx`,
            companyName
          });
        } else {
          await exportFinancialPositionToPdf({
            presentation,
            filename: `${exportFilenameBase}.pdf`,
            companyName
          });
        }
      } else if (reportType === 'cashFlow') {
        if (format === 'excel') {
          await exportCashFlowToExcel({
            presentation,
            filename: `${exportFilenameBase}.xlsx`,
            companyName
          });
        } else {
          await exportCashFlowToPdf({
            presentation,
            filename: `${exportFilenameBase}.pdf`,
            companyName
          });
        }
      } else if (reportType === 'equityChanges') {
        if (format === 'excel') {
          await exportEquityChangesToExcel({
            presentation,
            filename: `${exportFilenameBase}.xlsx`,
            companyName
          });
        } else {
          await exportEquityChangesToPdf({
            presentation,
            filename: `${exportFilenameBase}.pdf`,
            companyName
          });
        }
      } else if (reportType === 'notes') {
        if (format === 'excel') {
          await exportNotesToExcel({
            presentation,
            filename: `${exportFilenameBase}.xlsx`,
            companyName
          });
        } else {
          await exportNotesToPdf({
            presentation,
            filename: `${exportFilenameBase}.pdf`,
            companyName
          });
        }
      } else if (reportType === 'vukIfrsComparison') {
        if (format === 'excel') {
          await exportVukIfrsComparisonToExcel({
            presentation,
            filename: `${exportFilenameBase}.xlsx`,
            companyName
          });
        } else {
          await exportVukIfrsComparisonToPdf({
            presentation,
            filename: `${exportFilenameBase}.pdf`,
            companyName
          });
        }
      } else if (format === 'excel') {
        await exportProfitLossOciToExcel({
          presentation,
          filename: `${exportFilenameBase}.xlsx`,
          companyName
        });
      } else {
        await exportProfitLossOciToPdf({
          presentation,
          filename: `${exportFilenameBase}.pdf`,
          companyName
        });
      }
      setMessage(L(
        `${reportLabel} ${format.toUpperCase()} export created.`,
        `${reportLabel} ${format.toUpperCase()} export olusturuldu.`
      ));
    } catch (caught) {
      setError(caught.message || L('Export could not be created.', 'Export olusturulamadi.'));
    } finally {
      setExporting(false);
    }
  }, [L, clearFeedback, exportFilenameBase, presentation, reportLabel, reportType, selectionMeta.companyName]);

  const handleClearIfrsData = useCallback(async () => {
    if (!selection.companyId || !selection.periodId || !canClearIfrsData) {
      return;
    }
    const confirmed = window.confirm(
      L(
        'All IFRS data for this company and period will be deleted. Continue?',
        'Bu sirket ve donem icin tum IFRS verileri silinecek. Devam edilsin mi?'
      )
    );
    if (!confirmed) {
      return;
    }
    clearFeedback();
    setClearing(true);
    try {
      const result = await ifrsService.clearIfrsData({
        companyId: selection.companyId,
        periodId: selection.periodId
      });
      setReportResponse(null);
      setComparativeReportResponse(null);
      const removed = Object.values(result?.cleanupCounts || {}).reduce((sum, item) => sum + Number(item || 0), 0);
      setMessage(L(
        `IFRS data cleared. Removed rows: ${removed}.`,
        `IFRS verileri temizlendi. Silinen satir: ${removed}.`
      ));
    } catch (caught) {
      setError(caught.message || L('IFRS data could not be cleared.', 'IFRS verileri temizlenemedi.'));
    } finally {
      setClearing(false);
    }
  }, [L, canClearIfrsData, clearFeedback, selection.companyId, selection.periodId]);

  const metadataCards = useMemo(() => {
    if (!reportResponse) {
      return [];
    }
    if (reportType === 'vukIfrsComparison') {
      const isWorksheetDetailed = reportResponse.comparisonFormat === 'worksheetDetailed';
      return [
        {
          label: L('Batch Version', 'Batch Versiyonu'),
          value: isWorksheetDetailed ? 'WORKSHEET_V2' : 'APPROVED_BATCH'
        },
        {
          label: L('Rule Set', 'Kural Seti'),
          value: isWorksheetDetailed ? '1.0' : (reportResponse.ruleSetVersion || 'LEGACY')
        },
        {
          label: L('Calculation Engine', 'Hesaplama Motoru'),
          value: isWorksheetDetailed ? '1.0' : (reportResponse.calculationEngineVersion || 'LEGACY')
        },
        {
          label: L('Status', 'Durum'),
          value: reportResponse.status || '-'
        }
      ];
    }
    return [
      { label: L('Batch Version', 'Batch Versiyonu'), value: reportResponse.batchVersion || '-' },
      { label: L('Rule Set', 'Kural Seti'), value: reportResponse.ruleSetVersion || '-' },
      { label: L('Calculation Engine', 'Hesaplama Motoru'), value: reportResponse.calculationEngineVersion || '-' },
      { label: L('Status', 'Durum'), value: reportResponse.status || '-' }
    ];
  }, [L, reportResponse, reportType]);

  return (
    <div className="ifrs-workspace">
      <PageHeader
        title={reportLabel}
        description={L(
          'Load and review the approved IFRS report for the selected company and period.',
          'Secilen sirket ve donem icin onayli IFRS raporunu yukleyip inceleyin.'
        )}
      />

      <CompanyPeriodSelector onSelectionChange={handleSelectionChange} />

      {message ? <div className="ifrs-banner ifrs-banner-success">{message}</div> : null}
      {error ? <div className="ifrs-banner ifrs-banner-error">{error}</div> : null}

      <section className="card ifrs-card">
        <div className="card-header">
          <div>
            <h2>{reportLabel}</h2>
            <p>
              {supportsProfessionalPresentation
                ? L(
                    'Preview the statement in a comparative IFRS presentation before final revisions.',
                    'Nihai revizyon oncesinde raporu karsilastirmali IFRS sunumunda onizleyin.'
                  )
                : L(
                    'This module shows the selected IFRS report as a standalone screen.',
                    'Bu modul secilen IFRS raporunu bagimsiz ekran olarak gosterir.'
                  )}
            </p>
          </div>
        </div>

        <div className="ifrs-actions">
          <button type="button" className="btn-primary" disabled={!selection.companyId || !selection.periodId || loading} onClick={handleLoadReport}>
            {loading ? L('Loading...', 'Yukleniyor...') : L('Load Report', 'Raporu Yukle')}
          </button>
          {canClearIfrsData ? (
            <button
              type="button"
              className="btn-danger"
              disabled={!selection.companyId || !selection.periodId || clearing || loading}
              onClick={handleClearIfrsData}
            >
              {clearing ? L('Clearing...', 'Temizleniyor...') : L('Clear IFRS Data', 'IFRS Verisini Temizle')}
            </button>
          ) : null}
          {supportsProfessionalPresentation ? (
            <>
              <button type="button" className="btn-secondary" disabled={!presentation || exporting} onClick={() => handleExport('excel')}>
                {exporting ? L('Preparing...', 'Hazirlaniyor...') : L('Export Excel', 'Excel Export')}
              </button>
              <button type="button" className="btn-secondary" disabled={!presentation || exporting} onClick={() => handleExport('pdf')}>
                {exporting ? L('Preparing...', 'Hazirlaniyor...') : L('Export PDF', 'PDF Export')}
              </button>
            </>
          ) : null}
        </div>

        {supportsProfessionalPresentation && presentation ? (
          <div className="ifrs-financial-position">
            <div className="ifrs-report-meta-grid">
              {metadataCards.map((item) => (
                <div key={item.label} className="ifrs-report-meta-card">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>

            <div className="ifrs-statement-shell">
              <div className="ifrs-statement-header">
                <p className="ifrs-statement-eyebrow">{presentation.labels.reportTitle}</p>
                <h3>{selectionMeta.companyName || presentation.labels.statementName}</h3>
                <p className="ifrs-statement-note">{presentation.metadataNote}</p>
                {reportType === 'vukIfrsComparison' && presentation.isLegacyAudit ? (
                  <div className="ifrs-comparative-banner">
                    {L(
                      'Worksheet comparison is not available; approved batch audit comparison is shown.',
                      'Worksheet karsilastirmasi hazir degil; onayli batch audit karsilastirmasi gosteriliyor.'
                    )}
                  </div>
                ) : null}
                {!presentation.comparativeAvailable ? (
                  <div className="ifrs-comparative-banner">{presentation.labels.noComparative}</div>
                ) : null}
              </div>

              <div className="ifrs-table-wrap ifrs-financial-table-wrap">
                {presentation.viewType === 'comparison' ? (
                  <AuditComparisonTable presentation={presentation} />
                ) : presentation.viewType === 'notes' ? (
                  <NotesIndexView presentation={presentation} />
                ) : presentation.viewType === 'matrix' ? (
                  <EquityMatrixTable presentation={presentation} />
                ) : (
                  <table className="ifrs-financial-table">
                    <thead>
                      <tr>
                        <th>{presentation.labels.lineItem}</th>
                        <th>{presentation.labels.noteRef}</th>
                        <th className="ifrs-right">{presentation.currentPeriodLabel || presentation.labels.currentPeriod}</th>
                        <th className="ifrs-right">{presentation.previousPeriodLabel || presentation.labels.previousPeriod}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {presentation.sections.map((section) => (
                        section.groups.length > 0 ? (
                          <FragmentTableSection
                            key={section.key}
                            section={section}
                            locale={presentation.locale}
                          />
                        ) : null
                      ))}
                      {reportType === 'financialPosition' ? (
                        <>
                          <tr className="ifrs-line ifrs-line-summary">
                            <td>{presentation.totals.liabilities.label}</td>
                            <td />
                            <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.liabilities.current, presentation.locale)}</td>
                            <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.liabilities.previous, presentation.locale)}</td>
                          </tr>
                          <tr className="ifrs-line ifrs-line-summary">
                            <td>{presentation.totals.equity.label}</td>
                            <td />
                            <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.equity.current, presentation.locale)}</td>
                            <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.equity.previous, presentation.locale)}</td>
                          </tr>
                        </>
                      ) : null}
                      {(presentation.footerRows || []).map((row) => (
                        <tr key={row.label} className={resolveFooterRowClass(row.variant)}>
                          <td>{row.label}</td>
                          <td />
                          <td className="ifrs-right">{formatFinancialStatementAmount(row.current, presentation.locale)}</td>
                          <td className="ifrs-right">{formatFinancialStatementAmount(row.previous, presentation.locale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {reportResponse?.note ? (
                <details className="ifrs-report-details">
                  <summary>{L('Technical Note', 'Teknik Not')}</summary>
                  <p>{reportResponse.note}</p>
                  <p>
                    <strong>{L('Source Trace', 'Kaynak Iz')}:</strong> {mapSourceTrace(reportResponse.sourceTrace)}
                  </p>
                </details>
              ) : null}
            </div>
          </div>
        ) : null}

        {!supportsProfessionalPresentation && reportResponse?.lines?.length > 0 ? (
          <div className="ifrs-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{L('Code', 'Kod')}</th>
                  <th>{L('Name', 'Ad')}</th>
                  <th>{L('Type', 'Tur')}</th>
                  <th className="ifrs-right">{L('Amount', 'Tutar')}</th>
                  <th>{L('Source Trace', 'Kaynak Iz')}</th>
                </tr>
              </thead>
              <tbody>
                {reportResponse.lines.map((line) => (
                  <tr key={`${line.code}-${line.name}`}>
                    <td>{line.code}</td>
                    <td>{line.name}</td>
                    <td>{mapAccountType(line.accountType)}</td>
                    <td className="ifrs-right">{formatAmount(line.amount)}</td>
                    <td>{mapSourceTrace(line.sourceTrace)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function resolveFooterRowClass(variant) {
  if (variant === 'summary-strong') {
    return 'ifrs-line ifrs-line-subtotal ifrs-line-subtotal-strong';
  }
  if (variant === 'summary') {
    return 'ifrs-line ifrs-line-subtotal';
  }
  return 'ifrs-line ifrs-line-summary';
}

function NotesIndexView({ presentation }) {
  return (
    <div className="ifrs-notes-index">
      <div className="ifrs-notes-summary">
        <div className="ifrs-notes-summary-card">
          <span>{presentation.labels.noteCount}</span>
          <strong>{presentation.stats.noteCount}</strong>
        </div>
        <div className="ifrs-notes-summary-card">
          <span>{presentation.labels.tableCount}</span>
          <strong>{presentation.stats.tableCount}</strong>
        </div>
      </div>
      <div className="ifrs-notes-grid">
        {presentation.notes.map((note) => (
          <article key={note.code} className="ifrs-note-card">
            <div className="ifrs-note-card-header">
              <span>{note.code}</span>
              <strong>{note.title}</strong>
            </div>
            <div className="ifrs-note-card-meta">
              <span>{note.statusLabel}</span>
              <span>{presentation.labels.currentTables}: {note.currentTableCount}</span>
              <span>{presentation.labels.previousTables}: {note.previousTableCount}</span>
            </div>
            <div className="ifrs-note-card-body">
              <h4>{presentation.labels.tables}</h4>
              {note.tables.length > 0 ? (
                <ul>
                  {note.tables.map((table) => (
                    <li key={table.key}>
                      <strong>{table.code}</strong> {table.title}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{presentation.labels.notAvailable}</p>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function AuditComparisonTable({ presentation }) {
  if (presentation.comparisonVariant === 'worksheet') {
    return (
      <table className="ifrs-financial-table">
        <thead>
          <tr>
            <th>{presentation.labels.sourceType}</th>
            <th>{presentation.labels.sourceCode}</th>
            <th>{presentation.labels.sourceName}</th>
            <th className="ifrs-right">{presentation.labels.vukAmount}</th>
            <th className="ifrs-right">{presentation.labels.ifrsAmount}</th>
            <th className="ifrs-right">{presentation.labels.ifrsEffect}</th>
            <th>{presentation.labels.standardRef}</th>
            <th>{presentation.labels.explanation}</th>
          </tr>
        </thead>
        <tbody>
          {presentation.rows.map((row) => (
            <tr key={`${row.sourceType}-${row.sourceCode}`} className="ifrs-line ifrs-line-item">
              <td>{row.sourceType}</td>
              <td>{row.sourceCode}</td>
              <td>{row.sourceName}</td>
              <td className="ifrs-right">{formatFinancialStatementAmount(row.vukAmount, presentation.locale)}</td>
              <td className="ifrs-right">{formatFinancialStatementAmount(row.ifrsAmount, presentation.locale)}</td>
              <td className="ifrs-right">{formatFinancialStatementAmount(row.ifrsEffect, presentation.locale)}</td>
              <td>{row.standardRef}</td>
              <td>{row.explanation}</td>
            </tr>
          ))}
          <tr className="ifrs-line ifrs-line-summary">
            <td colSpan={3}>{presentation.labels.totalVukAmount}</td>
            <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.totalVukAmount, presentation.locale)}</td>
            <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.totalIfrsAmount, presentation.locale)}</td>
            <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.totalIfrsEffect, presentation.locale)}</td>
            <td colSpan={2} />
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table className="ifrs-financial-table">
      <thead>
        <tr>
          <th>{presentation.labels.sourceType}</th>
          <th>{presentation.labels.sourceCode}</th>
          <th>{presentation.labels.sourceName}</th>
          <th className="ifrs-right">{presentation.labels.vukAmount}</th>
          <th>{presentation.labels.ifrsAccount}</th>
          <th>{presentation.labels.ifrsAccountName}</th>
          <th className="ifrs-right">{presentation.labels.mappingAmount}</th>
          <th className="ifrs-right">{presentation.labels.recognitionAdjustment}</th>
          <th className="ifrs-right">{presentation.labels.measurementAdjustment}</th>
          <th className="ifrs-right">{presentation.labels.ifrsEffect}</th>
          <th>{presentation.labels.standardRef}</th>
          <th>{presentation.labels.explanation}</th>
        </tr>
      </thead>
      <tbody>
        {presentation.rows.map((row) => (
          <tr key={`${row.sourceType}-${row.sourceCode}-${row.ifrsAccount}`} className="ifrs-line ifrs-line-item">
            <td>{row.sourceType}</td>
            <td>{row.sourceCode}</td>
            <td>{row.sourceName}</td>
            <td className="ifrs-right">{formatFinancialStatementAmount(row.vukAmount, presentation.locale)}</td>
            <td>{row.ifrsAccount}</td>
            <td>{row.ifrsAccountName}</td>
            <td className="ifrs-right">{formatFinancialStatementAmount(row.mappingAmount, presentation.locale)}</td>
            <td className="ifrs-right">{formatFinancialStatementAmount(row.recognitionAdjustment, presentation.locale)}</td>
            <td className="ifrs-right">{formatFinancialStatementAmount(row.measurementAdjustment, presentation.locale)}</td>
            <td className="ifrs-right">{formatFinancialStatementAmount(row.ifrsEffect, presentation.locale)}</td>
            <td>{row.standardRef}</td>
            <td>{row.explanation}</td>
          </tr>
        ))}
        <tr className="ifrs-line ifrs-line-summary">
          <td colSpan={3}>{presentation.labels.totalVukAmount}</td>
          <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.totalVukAmount, presentation.locale)}</td>
          <td colSpan={2} />
          <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.totalMappingAmount, presentation.locale)}</td>
          <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.totalRecognitionAdjustment, presentation.locale)}</td>
          <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.totalMeasurementAdjustment, presentation.locale)}</td>
          <td className="ifrs-right">{formatFinancialStatementAmount(presentation.totals.totalIfrsEffect, presentation.locale)}</td>
          <td colSpan={2} />
        </tr>
      </tbody>
    </table>
  );
}

function EquityMatrixTable({ presentation }) {
  return (
    <table className="ifrs-financial-table">
      <thead>
        <tr>
          <th>{presentation.labels.lineItem}</th>
          {presentation.columns.map((column) => (
            <th key={column.key} className="ifrs-right">{column.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {presentation.rows.map((row) => (
          <tr key={row.key} className={resolveMatrixRowClass(row.variant)}>
            <td>{row.label}</td>
            {presentation.columns.map((column) => (
              <td key={`${row.key}-${column.key}`} className="ifrs-right">
                {formatFinancialStatementAmount(row.values[column.key] ?? 0, presentation.locale)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function resolveMatrixRowClass(variant) {
  if (variant === 'summary-strong') {
    return 'ifrs-line ifrs-line-subtotal ifrs-line-subtotal-strong';
  }
  if (variant === 'summary') {
    return 'ifrs-line ifrs-line-subtotal';
  }
  if (variant === 'total') {
    return 'ifrs-line ifrs-line-summary';
  }
  return 'ifrs-line ifrs-line-item';
}

function FragmentTableSection({ section, locale }) {
  return (
    <>
      <tr className="ifrs-line ifrs-line-section">
        <td colSpan={4}>{section.title}</td>
      </tr>
      {section.groups.map((group) => (
        <FragmentGroup key={group.key} group={group} locale={locale} />
      ))}
      <tr className="ifrs-line ifrs-line-total">
        <td>{section.totalLabel}</td>
        <td />
        <td className="ifrs-right">{formatFinancialStatementAmount(section.currentTotal, locale)}</td>
        <td className="ifrs-right">{formatFinancialStatementAmount(section.previousTotal, locale)}</td>
      </tr>
    </>
  );
}

function FragmentGroup({ group, locale }) {
  const rowClass = group.variant === 'summary-strong'
    ? 'ifrs-line ifrs-line-subtotal ifrs-line-subtotal-strong'
    : group.variant === 'summary'
      ? 'ifrs-line ifrs-line-subtotal'
      : 'ifrs-line ifrs-line-group';

  return (
    <>
      <tr className={rowClass}>
        <td>{group.title}</td>
        <td />
        <td className="ifrs-right">{formatFinancialStatementAmount(group.currentTotal, locale)}</td>
        <td className="ifrs-right">{formatFinancialStatementAmount(group.previousTotal, locale)}</td>
      </tr>
      {group.rows.map((row) => (
        <tr key={row.key} className="ifrs-line ifrs-line-item">
          <td>
            <div className="ifrs-line-label">
              <span>{row.name}</span>
            </div>
          </td>
          <td>{row.noteRef || ''}</td>
          <td className="ifrs-right">{formatFinancialStatementAmount(row.currentAmount, locale)}</td>
          <td className="ifrs-right">{formatFinancialStatementAmount(row.previousAmount, locale)}</td>
        </tr>
      ))}
    </>
  );
}

export default IfrsReportModulePage;
